import React, { useState, useEffect } from 'react';
import { Assessment, FAScores, Player, Report } from '../types';
import { reportService, DateRange } from '../services/reportService';
import { LineChart } from './charts/LineChart';
import { RadarChart } from './charts/RadarChart';
import { Loader2, Download, FileText, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const FA_MODEL = {
  technical: {
    title: 'Technical',
    color: '#ef4444',
    bgClass: 'bg-red-500',
    icon: 'T',
    attributes: ['ball_control', 'passing_accuracy', 'positioning', 'tactical_awareness', 'shooting', 'dribbling'],
  },
  physical: {
    title: 'Physical',
    color: '#eab308',
    bgClass: 'bg-yellow-500',
    icon: 'P',
    attributes: ['speed', 'endurance', 'strength', 'agility', 'coordination', 'balance'],
  },
  psychological: {
    title: 'Psychological',
    color: '#10b981',
    bgClass: 'bg-emerald-500',
    icon: 'M',
    attributes: ['confidence', 'resilience', 'decision_making', 'concentration', 'motivation', 'composure'],
  },
  social: {
    title: 'Social',
    color: '#3b82f6',
    bgClass: 'bg-blue-500',
    icon: 'S',
    attributes: ['teamwork', 'communication', 'respect', 'leadership', 'coachability', 'sportsmanship'],
  },
};

interface PlayerReportViewProps {
  player: Player;
  assessments: Assessment[];
  isParentView?: boolean;
}

export const PlayerReportView: React.FC<PlayerReportViewProps> = ({ player, assessments, isParentView = false }) => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [reports, setReports] = useState<Report[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingReports, setIsLoadingReports] = useState(true);

  useEffect(() => {
    loadPlayerReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.id]);

  const loadPlayerReports = async () => {
    try {
      setIsLoadingReports(true);
      const playerReports = await reportService.getPlayerReports(player.id);
      setReports(playerReports);
    } catch (error) {
      console.error('Error loading player reports:', error);
    } finally {
      setIsLoadingReports(false);
    }
  };

  const handleGenerateReport = async () => {
    try {
      setIsGenerating(true);
      const report = await reportService.generatePlayerReport(player.id, { dateRange });
      setReports([report, ...reports]);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadReport = async (reportId: string) => {
    try {
      await reportService.downloadReport(reportId);
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  const calcAvg = (scores: Record<string, number>): number => {
    const vals = Object.values(scores);
    return vals.reduce((s, v) => s + v, 0) / vals.length;
  };

  const getTrend = (cur: number, prev?: number): 'up' | 'down' | 'stable' => {
    if (!prev) return 'stable';
    const d = cur - prev;
    if (d > 0.5) return 'up';
    if (d < -0.5) return 'down';
    return 'stable';
  };

  const calcProgress = () => {
    if (assessments.length < 2) return { improved: 0, declined: 0, unchanged: 24 };
    const latest = assessments[0];
    const previous = assessments[1];
    let improved = 0, declined = 0, unchanged = 0;
    Object.entries(FA_MODEL).forEach(([key, cfg]) => {
      const ls = latest.scores[key as keyof FAScores] as Record<string, number>;
      const ps = previous.scores[key as keyof FAScores] as Record<string, number>;
      cfg.attributes.forEach((a) => {
        const c = ls[a] - ps[a];
        if (c > 0) improved++;
        else if (c < 0) declined++;
        else unchanged++;
      });
    });
    return { improved, declined, unchanged };
  };

  const fmtDate = (d: Date | string) =>
    new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const lineChartData = () => {
    if (assessments.length < 2) return [];
    const sorted = [...assessments].sort((a, b) => new Date(a.assessmentDate).getTime() - new Date(b.assessmentDate).getTime());
    return Object.entries(FA_MODEL).map(([key, cfg]) => ({
      label: cfg.title,
      color: cfg.color,
      data: sorted.map((a, i) => ({
        x: i,
        y: calcAvg(a.scores[key as keyof FAScores] as Record<string, number>),
        label: cfg.title,
        date: fmtDate(a.assessmentDate),
      })),
    }));
  };

  const radarData = () => {
    if (!latest) return [];
    return Object.entries(FA_MODEL).map(([key, cfg]) => ({
      label: cfg.title,
      value: calcAvg(latest.scores[key as keyof FAScores] as Record<string, number>),
      color: cfg.color,
    }));
  };

  const latest = assessments[0];
  const previous = assessments[1];
  const progress = calcProgress();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isParentView ? `${player.firstName}'s Progress Report` : `${player.firstName} ${player.lastName}`}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {assessments.length} assessment{assessments.length !== 1 ? 's' : ''} · Last updated: {latest ? fmtDate(latest.assessmentDate) : 'Never'}
            </p>
          </div>
          {!isParentView && (
            <button
              onClick={handleGenerateReport}
              disabled={isGenerating || assessments.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              {isGenerating ? 'Generating...' : 'Generate PDF'}
            </button>
          )}
        </div>

        {!isParentView && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-4 pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-500">Report period:</span>
            <input
              type="date"
              value={dateRange?.startDate ? dateRange.startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: new Date(e.target.value) } as DateRange))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none min-h-[44px]"
            />
            <span className="text-sm text-gray-400">to</span>
            <input
              type="date"
              value={dateRange?.endDate ? dateRange.endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: new Date(e.target.value) } as DateRange))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none min-h-[44px]"
            />
          </div>
        )}
      </div>

      {assessments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No assessments yet</h3>
          <p className="text-sm text-gray-500">Complete the first assessment to see progress reports and visualisations.</p>
        </div>
      ) : (
        <>
          {/* Progress Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{progress.improved}</p>
                <p className="text-xs text-gray-500 uppercase font-medium mt-1">Improved</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{progress.declined}</p>
                <p className="text-xs text-gray-500 uppercase font-medium mt-1">Declined</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-500">{progress.unchanged}</p>
                <p className="text-xs text-gray-500 uppercase font-medium mt-1">Unchanged</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{assessments.length}</p>
                <p className="text-xs text-gray-500 uppercase font-medium mt-1">Assessments</p>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Over Time</h3>
              {lineChartData().length > 0 ? (
                <LineChart data={lineChartData()} />
              ) : (
                <div className="h-64 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
                  <p className="text-sm text-gray-400">Need at least 2 assessments to show progress</p>
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Skills Assessment</h3>
              {latest ? (
                <RadarChart data={radarData()} />
              ) : (
                <div className="h-64 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
                  <p className="text-sm text-gray-400">Complete an assessment to see current scores</p>
                </div>
              )}
            </div>
          </div>

          {/* Skills Development Analysis */}
          {latest && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills Development Analysis</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(FA_MODEL).map(([key, cfg]) => {
                  const curScores = latest.scores[key as keyof FAScores] as Record<string, number>;
                  const prevScores = previous?.scores[key as keyof FAScores] as Record<string, number>;
                  const curAvg = calcAvg(curScores);
                  const prevAvg = prevScores ? calcAvg(prevScores) : undefined;
                  const trend = getTrend(curAvg, prevAvg);
                  return (
                    <div key={key} className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 ${cfg.bgClass} rounded-full flex items-center justify-center text-white text-sm font-bold`}>
                          {cfg.icon}
                        </div>
                        <span className="font-semibold text-gray-900">{cfg.title}</span>
                      </div>
                      <p className="text-xl font-bold text-gray-900">{curAvg.toFixed(1)}/10</p>
                      <div className={`flex items-center gap-1 mt-1 text-sm font-medium ${
                        trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-400'
                      }`}>
                        {trend === 'up' ? <TrendingUp className="h-4 w-4" /> : trend === 'down' ? <TrendingDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                        {trend === 'up' ? 'Improving' : trend === 'down' ? 'Needs attention' : 'No change'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Recent Reports */}
      {!isParentView && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Reports</h3>
          {isLoadingReports ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-green-600" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">No reports generated yet. Generate your first report above.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.slice(0, 5).map((report) => {
                const expired = reportService.isReportExpired(report);
                return (
                  <div key={report.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {reportService.formatReportType(report.type)} — {fmtDate(report.createdAt)}
                      </p>
                      <p className={`text-xs mt-0.5 ${expired ? 'text-red-500' : 'text-green-600'}`}>
                        {expired ? 'Expired' : 'Available'}
                      </p>
                    </div>
                    <button
                      disabled={expired}
                      onClick={() => handleDownloadReport(report.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
